import type { TherapistOfficeDistrictItem } from '@hedepy/common/schemas/routes/api/v2/therapist'
import { LocationIcon } from '@hedepy/icons/refactor/Location'
import { RocketIcon } from '@hedepy/icons/refactor/Rocket'
import { useMemo } from 'react'

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/legacy-src/components/ui/select'
import { LOCALE } from '@/legacy-src/constants/locale.constants'
import { getTranslate } from '@/legacy-src/utilities/i18n'

import { ClinicTherapistSessionLocationFilter } from '@/src/domains/therapist-filters/components/ClinicTherapistSessionLocationFilter'
import { TherapistFilterMenuGroupUI } from '@/src/domains/therapist-filters/components/TherapistFilterMenuGroup'
import { TherapistFilterMenuLabelUI } from '@/src/domains/therapist-filters/components/TherapistFilterMenuLabel'
import { TherapistFilterMenuSeparatorUI } from '@/src/domains/therapist-filters/components/TherapistFilterMenuSeparator'
import { TherapistSessionLocationFilterItemUI } from '@/src/domains/therapist-filters/components/TherapistSessionLocationFilterItem'

const MAX_VISIBLE_POPULAR_DISTRICTS = 3

export interface TherapistSessionLocationFilterUIProps {
  checked: number[]
  region?: string
  onRegionChange: (region: string) => void
  onCheckedChange: (checked: boolean, id: number) => void
  districts: TherapistOfficeDistrictItem[]
}

export const TherapistSessionLocationFilterUI = ({
  checked,
  region,
  onRegionChange,
  onCheckedChange,
  districts,
}: TherapistSessionLocationFilterUIProps) => {
  const { t } = getTranslate()

  const regularDistricts = useMemo(() => districts.filter(({ clinic }) => !clinic), [districts])
  const popularDistricts = useMemo(() => regularDistricts.filter((location) => location.frequent), [regularDistricts])
  const regions = useMemo(
    () =>
      [...new Set(regularDistricts.map((location) => location.regionName))].sort((a, b) => a.localeCompare(b, LOCALE)),
    [regularDistricts],
  )
  const selectedRegionDistricts = useMemo(
    () =>
      regularDistricts
        .filter((location) => location.regionName === region)
        .sort((a, b) => a.districtName.localeCompare(b.districtName, LOCALE)),
    [regularDistricts, region],
  )

  return (
    <div className="flex w-full flex-col bg-white">
      <ClinicTherapistSessionLocationFilter checked={checked} onCheckedChange={onCheckedChange} districts={districts} />
      {popularDistricts.length > 0 ? (
        <>
          <TherapistFilterMenuGroupUI className="m-5">
            <TherapistFilterMenuLabelUI Icon={RocketIcon}>
              {t('component.therapist.filter.event-location.popular.title')}
            </TherapistFilterMenuLabelUI>
            <div className="flex flex-col gap-5">
              {popularDistricts.map(({ id, districtName, regionName }, index) => {
                return (
                  <TherapistSessionLocationFilterItemUI
                    key={id}
                    name={districtName}
                    regionName={regionName}
                    popular={index < MAX_VISIBLE_POPULAR_DISTRICTS}
                    displayRegion
                    checked={checked.includes(id)}
                    onChange={(checked) => {
                      onCheckedChange(checked, id)
                    }}
                  />
                )
              })}
            </div>
          </TherapistFilterMenuGroupUI>
          <TherapistFilterMenuSeparatorUI />
        </>
      ) : null}
      <TherapistFilterMenuGroupUI className="m-5">
        <TherapistFilterMenuLabelUI Icon={LocationIcon}>
          {t('component.therapist.filter.event-location.all.title')}
        </TherapistFilterMenuLabelUI>
        <Select value={region} onValueChange={onRegionChange}>
          <SelectTrigger size="sm">
            <SelectValue placeholder={t('component.therapist.filter.event-location.all.placeholder')} />
          </SelectTrigger>
          <SelectContent>
            {regions.map((option) => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex flex-col gap-5">
          {selectedRegionDistricts.map(({ id, districtName, regionName }) => (
            <TherapistSessionLocationFilterItemUI
              key={id}
              name={districtName}
              regionName={regionName}
              checked={checked.includes(id)}
              onChange={(checked) => {
                onCheckedChange(checked, id)
              }}
            />
          ))}
        </div>
      </TherapistFilterMenuGroupUI>
    </div>
  )
}
